import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Users2, ArrowUpRight, Check, Pencil } from "lucide-react";
import { toast } from "sonner";

const SCORE_KEYS = ["readiness", "sharpness", "effort", "rest"];

function Score({ label, val, good }) {
  return (
    <div className="text-center">
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-heading font-black tabular-nums ${good === false ? "text-traffic-red" : good === true ? "text-traffic-green" : ""}`}>{val ?? "-"}</div>
    </div>
  );
}

export default function SquadOptimizer({ embedded, squad="", academyId="" }) {
  const [athletes, setAthletes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [subBuf, setSubBuf] = useState("");
  const navigate = useNavigate();

  const load = () => api.get("/athletes").then((r) => setAthletes(r.data.filter(a=>(!squad || (a.sub_team || a.squad || a.sport)===squad) && (!academyId || (a.academy_id || '__unaffiliated__')===academyId))));
  useEffect(() => { load(); }, [squad,academyId]);

  const setMatchDay = async (id, date) => {
    if (!date) return;
    await api.post(`/athletes/${id}/match-day?target_date=${date}`);
    toast.success("Match day set · simulator & plan snap to it");
    load();
  };
  const saveSubTeam = async (id) => {
    await api.post(`/athletes/${id}/team`, { sub_team: subBuf });
    toast.success("Sub-team updated"); setEditing(null); load();
  };

  // group by sport -> sub_team
  const bySport = {};
  athletes.forEach((a) => {
    const sport = a.sport || "Unassigned";
    const sub = a.sub_team || "-";
    bySport[sport] = bySport[sport] || {};
    (bySport[sport][sub] = bySport[sport][sub] || []).push(a);
  });

  return (
    <div>
      {!embedded && (
      <header className="min-h-16 border-b border-border flex flex-wrap items-center py-3 px-8 gap-4 bg-background">
        <Users2 className="w-5 h-5" />
        <h1 className="font-heading text-xl font-black tracking-tight">Roster</h1>
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Roster by sport & sub-team · set match days</span>
        <span className="ml-auto text-xs text-muted-foreground tabular-nums" data-testid="team-count">{athletes.length} athletes</span>
      </header>
      )}

      {athletes.length === 0 ? <div className="p-8 text-sm text-muted-foreground">No athletes yet.</div> : (
        <div className="p-8 space-y-10" data-testid="team-groups">
          {Object.entries(bySport).sort().map(([sport, subs]) => (
            <div key={sport} data-testid={`sport-group-${sport}`}>
              <h2 className="font-heading text-lg font-black tracking-tight mb-4 pb-2 border-b border-border">{sport} <span className="text-sm text-muted-foreground font-normal">· {Object.values(subs).flat().length}</span></h2>
              {Object.entries(subs).sort().map(([sub, list]) => (
                <div key={sub} className="mb-6" data-testid={`subteam-${sport}-${sub}`}>
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">{sub === "-" ? "No sub-team" : sub} · {list.length}</div>
                  <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {list.map((a) => {
                      const st = a.status || {};
                      return (
                        <div key={a.athlete_id} className="border border-border" data-testid={`team-card-${a.athlete_id}`}>
                          <div className="px-4 py-3 border-b border-border flex items-center gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="font-medium truncate">{a.name}</div>
                              <div className="text-xs text-muted-foreground truncate">{a.sport}</div>
                            </div>
                            <span className={`w-2.5 h-2.5 rounded-full ${a.overall_status === "green" ? "bg-traffic-green" : "bg-traffic-red"}`} title={a.overall_status} />
                            <button data-testid={`team-open-${a.athlete_id}`} onClick={() => navigate(`/athlete/${a.athlete_id}`)} className="text-muted-foreground hover:text-foreground transition-colors"><ArrowUpRight className="w-4 h-4" /></button>
                          </div>
                          <div className="grid grid-cols-4 gap-2 p-3 border-b border-border">
                            {SCORE_KEYS.map((k) => <Score key={k} label={k.slice(0, 3).toUpperCase()} val={a.scores?.[k] != null ? Math.round(a.scores[k]) : null} good={st[k]?.good} />)}
                          </div>
                          <div className="p-3 flex items-center gap-2 flex-wrap">
                            <label className="text-xs text-muted-foreground flex items-center gap-1.5">Match day
                              <input type="date" data-testid={`team-matchday-${a.athlete_id}`} defaultValue={a.target_date || ""} onChange={(e) => setMatchDay(a.athlete_id, e.target.value)} className="border border-border px-2 py-1 text-xs" />
                            </label>
                            {editing === a.athlete_id ? (
                              <span className="flex items-center gap-1 ml-auto">
                                <input data-testid={`subteam-input-${a.athlete_id}`} value={subBuf} onChange={(e) => setSubBuf(e.target.value)} placeholder="sub-team" className="border border-border px-2 py-1 text-xs w-28" />
                                <button data-testid={`subteam-save-${a.athlete_id}`} onClick={() => saveSubTeam(a.athlete_id)} className="text-traffic-green"><Check className="w-4 h-4" /></button>
                              </span>
                            ) : (
                              <button data-testid={`subteam-edit-${a.athlete_id}`} onClick={() => { setEditing(a.athlete_id); setSubBuf(a.sub_team || ""); }} className="ml-auto text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"><Pencil className="w-3 h-3" /> {a.sub_team || "set sub-team"}</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
